import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import WorkCard from '@/components/base/WorkCard';
import { useSEO } from '@/hooks/useSEO';
import { works, type WorkType } from '@/mocks/works';
import { searchWorks, sortByHot } from '@/utils/works';

type Tab = 'all' | WorkType;

const TABS: { key: Tab; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'tv', label: '电视剧' },
  { key: 'movie', label: '电影' },
];

const suggestTerms = Array.from(
  new Set([...works.map((w) => w.title), ...works.flatMap((w) => w.actors)])
);

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [input, setInput] = useState(q);
  const [tab, setTab] = useState<Tab>('all');
  const [focused, setFocused] = useState(false);
  const blurTimer = useRef<number | null>(null);

  useSEO({
    title: q ? `搜索“${q}” - 年轻的岳母` : '搜索影视 - 年轻的岳母',
    description: `在年轻的岳母搜索影视作品，当前关键词：${q || '（未输入）'}。支持按剧名、演员、导演与类型查找。`,
    keywords: `年轻的岳母,影视搜索,${q || '影视大全'}`,
  });

  useEffect(() => {
    setInput(q);
    setTab('all');
  }, [q]);

  const allResults = useMemo(() => searchWorks(q), [q]);
  const results = tab === 'all' ? allResults : allResults.filter((w) => w.type === tab);

  const hotList = useMemo(() => sortByHot(works).slice(0, 8), []);

  const suggestions = useMemo(() => {
    const kw = input.trim().toLowerCase();
    if (!kw) return [];
    return suggestTerms.filter((t) => t.toLowerCase().includes(kw)).slice(0, 6);
  }, [input]);

  const runSearch = (value: string) => {
    const kw = value.trim();
    if (kw) setParams({ q: kw });
    else setParams({});
    setFocused(false);
  };

  const counts: Record<Tab, number> = {
    all: allResults.length,
    tv: allResults.filter((w) => w.type === 'tv').length,
    movie: allResults.filter((w) => w.type === 'movie').length,
  };

  return (
    <Layout>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">影视搜索</h1>
          <p className="mt-2 text-sm text-foreground-600">
            {q ? (
              <>
                搜索关键词：<strong className="text-accent-600">{q}</strong>
              </>
            ) : (
              '输入剧名、演员或类型，快速定位你感兴趣的影视作品'
            )}
          </p>

          <div className="relative mt-5 max-w-2xl">
            <div className="flex items-center bg-background-50 border border-background-300 rounded-full pl-4 pr-1.5 h-12 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100 transition-all">
              <i className="ri-search-line text-foreground-400 text-lg" aria-hidden="true"></i>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                  blurTimer.current = window.setTimeout(() => setFocused(false), 150);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (blurTimer.current) window.clearTimeout(blurTimer.current);
                    runSearch(input);
                  }
                }}
                placeholder="搜索剧名、演员或类型"
                className="flex-1 bg-transparent text-sm md:text-base px-3 outline-none text-foreground-900 placeholder:text-foreground-400"
                aria-label="搜索影视"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => {
                    setInput('');
                    setParams({});
                  }}
                  className="w-7 h-7 mr-1 rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-100 flex items-center justify-center cursor-pointer"
                  aria-label="清空关键词"
                >
                  <i className="ri-close-line" aria-hidden="true"></i>
                </button>
              )}
              <button
                type="button"
                onClick={() => runSearch(input)}
                className="h-9 px-5 rounded-full bg-accent-500 text-secondary-950 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </div>

            {focused && suggestions.length > 0 && (
              <ul className="absolute z-30 mt-2 w-full bg-background-50 border border-background-200 rounded-lg overflow-hidden py-1">
                {suggestions.map((s) => (
                  <li key={s}>
                    <button
                      type="button"
                      onMouseDown={() => {
                        if (blurTimer.current) window.clearTimeout(blurTimer.current);
                        setInput(s);
                        runSearch(s);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 flex items-center gap-2 cursor-pointer"
                    >
                      <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
                      {s}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {q ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center bg-background-100 rounded-full p-1">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      tab === t.key
                        ? 'bg-primary-600 text-background-50 font-medium'
                        : 'text-foreground-600 hover:text-primary-700'
                    }`}
                  >
                    {t.label}
                    <span className="ml-1 text-xs opacity-80">{counts[t.key]}</span>
                  </button>
                ))}
              </div>
              <p className="text-sm text-foreground-600">
                共找到 <strong className="text-accent-600">{results.length}</strong> 条结果
              </p>
            </div>

            {results.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {results.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
            ) : (
              <div className="mt-8 py-14 flex flex-col items-center text-center rounded-lg bg-background-50 border border-background-200">
                <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
                  <i className="ri-emotion-sad-line" aria-hidden="true"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                  未找到相关影视内容
                </h2>
                <p className="mt-1.5 text-sm text-foreground-500">
                  换个关键词试试，或看看下面的热门影视
                </p>
                <div className="mt-6 w-full max-w-3xl text-left">
                  <h3 className="text-sm font-medium text-foreground-800 mb-3">热门影视推荐</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {hotList.slice(0, 4).map((w) => (
                      <Link
                        key={w.id}
                        to={`/detail/${w.id}`}
                        className="flex items-center gap-2 p-2 rounded-md bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-14 shrink-0 rounded overflow-hidden bg-background-200">
                          <img
                            src={w.cover}
                            alt={w.title}
                            title={`${w.title} 热门影视`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <span className="text-xs text-foreground-800 line-clamp-2">{w.title}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-4">
            <div className="flex items-center gap-2 mb-5">
              <i className="ri-fire-line text-accent-500 text-xl" aria-hidden="true"></i>
              <h2 className="font-heading text-lg font-bold text-foreground-950">热门搜索 / 大家都在看</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {hotList.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}